import isEqual from 'lodash/isEqual';
import capitalize from 'lodash/capitalize';
import {Location} from 'react-router';

import * as InsightsOverrides from 'common/report/insights/util/bookmark-overrides';
import {
  FormulaShowClause,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {SegmentFilterController, ToggleCallbackArgs} from 'common/charts/contexts/segment-filter-controller';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {getDateRange} from 'common/report/insights/util/time';
import {AdditionalContext, QueryContext, SegmentationMetadata} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {canUseMultiAttribution} from 'common/widgets/multi-attribution-menu/utils';
import {prompt} from 'common/components/confirm';
import {bulkUpdateMetrics, showMetricsApiErrorToast} from 'common/report/queries/app-api/metrics';
import {Metric, MetricsResultsMap} from 'app_api/projects/metrics/types';
import {getMetricUpdateFromFormulaShowClause} from 'common/query-builder/query-entry-section/row-types/formula-entry/utils';
import {isModifiedShowClauseFormula} from 'common/util/formulas';
import {ModelStatus} from 'common/util/model-resource/model-resource';

import {ReportManager} from '../report-manager';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {getReportTracker} from '../report-manager/tracking';
import {ChartType, ProjectFeatureFlag, ReportName, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from '../constants';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ProjectDetails} from '../stores/project-details';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {QueryContextFnParams} from '../report-manager/types';
import {SortBy} from '../util/sorting/types';
import {DisplayBookmark} from '../dashboards/types';
import {useSingleReport} from '../util/feature-sampling';
import {IronContextRegistry} from '../context-registry';
import {
  getQuerySamplingFactor,
  getTimeComparisonTrackingData,
  hasValidMetricBookmarkParams,
  isEmptyFunnelResult,
  isEmptyResult,
  isEmptyRetentionResult,
} from './util';
import {getReportFromQueryContext, getReportQueryContext, InsightsQueryBuilderEl} from './query-builder';
import {InsightsReportHeaderEl} from './report-header';
import {InsightsQueryManager} from './query';
import {InsightsVisualizationEl} from './visualization';
import {handleSegmentFilterControllerChange} from './visualization/util';
import {Report} from './models/report';
import {PRODUCTION_TRACKING_EVENTS} from './tracking';
import {getSelectedSegments} from './util/segments';
import {hasDataTable} from './visualization/charts/chart-display/utils';
import {
  hasSavedFormula,
  isFormulaShowClause,
  isFunnelOnlyInsightsBookmark,
  isRetentionOnlyInsightsBookmark,
  transformBookmarkToMultiMetricBookmark,
} from './models/utils';
import {QueryMode} from './util/constants';

export class InsightsReportManager extends ReportManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  bookmarkType = BookmarkType.Insights;
  reportDisplayName = `Insights`;

  tracker = getReportTracker({
    appName: ReportName.Insights,
    productionEvents: PRODUCTION_TRACKING_EVENTS,
    productionPrefix: `Insights`,
  });

  isCsvExportEnabled = true;

  queryBuilderEl = InsightsQueryBuilderEl;
  reportHeaderEl = InsightsReportHeaderEl;
  visualizationEl = InsightsVisualizationEl;

  queryManager = new InsightsQueryManager({
    projectId: this.projectId,
    workspaceId: this.workspaceId,
    metricsResource: this.metricsResource,
  });

  async csvExportQuery({
    bookmark,
    currentProject,
    useQueryCache = true,
    location,
  }: {
    bookmark: DisplayBookmark;
    currentProject: CurrentProject;
    useQueryCache?: boolean;
    location: Location;
  }) {
    const {result, filename} = await this.queryManager.csvExportQuery({
      bookmarkParams: bookmark.params,
      currentProject,
      useQueryCache,
      location,
    });
    const reportName = bookmark.name || `Untitled Report`;
    const filenameSuffix = filename ? `_${filename}` : `.csv`;
    return {
      result,
      filename: `${reportName}${filenameSuffix}`,
    };
  }

  public getAnalysisDescription(params: InsightsBookmarkParams): string {
    return capitalize(params.displayOptions.analysis);
  }

  public hasUnsavedBookmarkParams(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    return !isEqual(
      Report.fromBookmarkData(original).toBookmarkData(),
      Report.fromBookmarkData(edited).toBookmarkData(),
    );
  }

  /** commits saves for an insights bookmark with unsaved formulas edits */
  public async handleBookmarkSave(
    bookmark: InsightsBookmarkParams,
    availableContexts: IronContextRegistry,
  ): Promise<null | InsightsBookmarkParams> {
    const canEditFormula = (clause: FormulaShowClause) => {
      const model = availableContexts?.metrics?.getOne(clause.formula.id);
      return model?.status === ModelStatus.Success && model.value.can_update_basic === true;
    };

    /** the behavior when saving a report with restricted formulas is to ignore these changes and notify the user */
    let hasRestrictedFormulaEdits = false;
    const unsavedFormulas = bookmark.sections.show.reduce((formulas, clause, index) => {
      if (isFormulaShowClause(clause) && isModifiedShowClauseFormula(clause.formula)) {
        if (canEditFormula(clause) === false) {
          hasRestrictedFormulaEdits = true;
        }

        formulas.set(index, clause);
      }
      return formulas;
    }, new Map<number, FormulaShowClause>());

    if (unsavedFormulas.size === 0) {
      return bookmark;
    }

    const {didConfirm} = await prompt({
      title: `Save All Changes?`,
      description: `You have edited formulas used in this report.\nWould you like to save edits made to ${
        hasRestrictedFormulaEdits ? `formulas you have permissions for` : `formulas`
      } in addition to changes made to the report?`,
      'confirm-button-label': `Save All Changes`,
      'cancel-button-label': `Cancel`,
    });

    if (didConfirm === false) {
      return null;
    }

    const metrics: Metric[] = Array.from(unsavedFormulas.values())
      .filter(canEditFormula)
      .map(getMetricUpdateFromFormulaShowClause);

    let results: MetricsResultsMap;
    try {
      results = await bulkUpdateMetrics(metrics);
    } catch {
      results = null;
      showMetricsApiErrorToast();
    }

    if (results === null) {
      return null;
    }

    availableContexts?.metrics?.update(results, {partialUpdate: false});

    for (const [index, unsavedFormula] of unsavedFormulas) {
      bookmark.sections.show[index] = {
        formula: {id: unsavedFormula.formula.id},
        isExpanded: false,
      };
    }

    this.track(
      `[mp-query-builder] Save Formulas on Report Save`,
      this.getClientQueryTrackingProps({params: bookmark, availableContexts}),
    );

    return bookmark;
  }

  public getDefaultBookmarkParams(project: CurrentProject): InsightsBookmarkParams {
    return this.getDefaultReport(project).toBookmarkData();
  }

  public isQueryable(params: InsightsBookmarkParams) {
    return hasValidMetricBookmarkParams(params.sections.show);
  }

  public isEmptyResults(results: InsightsQueryResults, params: InsightsBookmarkParams) {
    const chartType = params.displayOptions.chartType;
    if (isRetentionOnlyInsightsBookmark(params) && chartType === ChartType.RetentionCurve) {
      return isEmptyRetentionResult(results);
    }
    if (isFunnelOnlyInsightsBookmark(params) && chartType === ChartType.FunnelSteps) {
      return isEmptyFunnelResult(results, params);
    }
    return isEmptyResult(results);
  }

  public getSegmentationMetadatas(results: InsightsQueryResults): SegmentationMetadata[] {
    const metas = results.meta?.report_sections?.group;
    return metas?.map?.((meta) => ({
      segment: getSelectedSegments([new GroupClause(meta.bookmark)])[0],
      numericBucket: meta.numeric_buckets,
    }));
  }

  public handleSegmentFilterControllerChange(
    segmentFilterController: SegmentFilterController,
    params: InsightsBookmarkParams,
    project: CurrentProject,
    toggleCallbackArgs: ToggleCallbackArgs,
  ) {
    const resultParams = handleSegmentFilterControllerChange(segmentFilterController, params);

    // tracks selection changes for reports with inline formulas
    if (params.sections?.show?.some((clause) => isFormulaShowClause(clause))) {
      const trackingProps = {
        'selection type': toggleCallbackArgs.trackingInfo.toggleType,
        'is formula selection': toggleCallbackArgs.trackingInfo.isFormula,
        ...this.getClientQueryTrackingProps({params, project}),
      };
      this.track(`[simple formulas] Selection Change`, trackingProps);
    }

    return resultParams;
  }

  public shouldResetSegmentFilterSelectMode(original: InsightsBookmarkParams, edited: InsightsBookmarkParams) {
    const cohortsChanged = !isEqual(original.sections.cohorts, edited.sections.cohorts);
    const groupsChanged = !isEqual(original.sections.group, edited.sections.group);

    return cohortsChanged || groupsChanged;
  }

  public getDateRange(params: InsightsBookmarkParams): DateRange {
    const report = Report.fromBookmarkData(params);
    return getDateRange(report.sections.time.clauses);
  }

  public getBookmarkParamsFromQueryContext(
    context: QueryContext,
    params: QueryContextFnParams,
  ): InsightsBookmarkParams {
    return getReportFromQueryContext(context, params, this.insightsReportMode, this.metricsResource).toBookmarkData();
  }

  public getQueryContextFromBookmarkParams({
    params,
    originDashboard,
    querySamplingEnabled,
    project,
    dataGroups,
    additionalContext,
  }: {
    params: InsightsBookmarkParams;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
    project: CurrentProject | ProjectDetails;
    dataGroups: DataGroup[];
    additionalContext: AdditionalContext;
  }): QueryContext {
    const report = Report.fromBookmarkData(params);
    report.querySamplingEnabled = querySamplingEnabled;
    const extraParams = useSingleReport()
      ? {
          metricType: additionalContext?.metricType,
          chartType: additionalContext?.chartType,
        }
      : {};
    return getReportQueryContext(report, {
      defaultReportState: this.getDefaultReport(project),
      originDashboard,
      dataGroups,
      additionalContext,
      metricsResource: this.metricsResource,
      ...extraParams,
    });
  }

  public hasDataTable(params: InsightsBookmarkParams): boolean {
    return hasDataTable(params.displayOptions?.chartType);
  }

  public getQuerySamplingFactor(results: InsightsQueryResults): number {
    return getQuerySamplingFactor(results);
  }

  public getClientQueryTrackingProps({
    params,
    project,
    error,
    availableContexts,
  }: {
    params: InsightsBookmarkParams;
    project?: CurrentProject;
    error?: InsightsQueryError;
    availableContexts?: Partial<IronContextRegistry>;
  }): ClientQueryTrackingProps {
    const report = Report.fromBookmarkData(params);
    let timeComparisonTrackingData = null;
    if (report.timeComparison) {
      timeComparisonTrackingData = getTimeComparisonTrackingData({
        timeClause: report.sections.time.clauses[0],
        utcOffset: project?.utcOffset,
        timeComparison: report.timeComparison,
      });
    }

    const extraTrackingProps = {};
    const {chartType} = params.displayOptions;
    if (chartType === ChartType.Table) {
      const sortConfig = params.sorting[chartType];
      extraTrackingProps[`Has Modified View N Limit`] =
        (sortConfig.sortBy === SortBy.Value &&
          Number.isFinite(sortConfig.viewNLimit) &&
          sortConfig.viewNLimit !== VIEW_N_DEFAULT_LIMIT) ||
        (sortConfig.sortBy === SortBy.Column &&
          sortConfig.colSortAttrs.some(
            (colSortAttr) => Number.isFinite(colSortAttr.viewNLimit) && colSortAttr.viewNLimit !== VIEW_N_DEFAULT_LIMIT,
          ));
    }

    return {
      ...report.toTrackingData({error, metricsResource: availableContexts?.metrics}),
      ...report.toGroupAnalyticsTrackingData({metricsResource: this.metricsResource}),
      ...report.toLookupTableTrackingData(),
      ...timeComparisonTrackingData,
      'has pivot table enabled': project?.hasFeatureFlag(ProjectFeatureFlag.InsightsPivotTable),
      'Can Use Multi-Touch Attribution': canUseMultiAttribution(project),
      ...extraTrackingProps,
    };
  }

  private getDefaultReport(project: CurrentProject | ProjectDetails) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      project?.hasFeatureFlag(flag),
    );

    return Report.createDefaultReport({
      queryMode: project?.isPeopleOnlyProject()
        ? QueryMode.Groups
        : this.insightsReportMode === BookmarkType.Funnels
        ? QueryMode.Funnels
        : this.insightsReportMode === BookmarkType.Retention
        ? QueryMode.Retention
        : QueryMode.Events,
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
    });
  }

  public processBookmarkParams({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}): InsightsBookmarkParams {
    bookmarkParams = transformBookmarkToMultiMetricBookmark(bookmarkParams);
    bookmarkParams = InsightsOverrides.updateSortConfig(bookmarkParams, {suppressMigrationAlert: false});
    return bookmarkParams;
  }

  public getCommonChartType(params: InsightsBookmarkParams): ChartType {
    return params.displayOptions?.chartType;
  }

  async waitForApiResources({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}) {
    if (hasSavedFormula(bookmarkParams)) {
      await this.metricsResource.waitForIndexFetch();
    }
  }
}
